import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, ChevronLeft, Trash2, ChevronDown, Globe, Search, X } from 'lucide-react';
import { MODE_CATEGORIES } from '../../constants/modeCategories';
import { LANGUAGES } from '../../constants/modes';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ScrollArea } from '../ui/scroll-area';
import { toast } from 'sonner';
import axios from 'axios';

const API = '/api';

function timeAgo(isoString) {
  if (!isoString) return '';
  const diff = Date.now() - new Date(isoString).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(isoString).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

export const Sidebar = ({
  sessions,
  selectedMode,
  onModeSelect,
  onNewChat,
  currentSessionId,
  isOpen,
  onToggle,
  language,
  onLanguageChange,
  onSessionsChange,
  onClose,
  onNewSession,
  onSessionSelect,
}) => {
  const navigate = useNavigate();
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredModes = useMemo(() => {
    if (!searchQuery.trim()) return null;
    const q = searchQuery.toLowerCase();
    const results = [];
    MODE_CATEGORIES.forEach(category => {
      const matched = category.modes.filter(m => m.label.toLowerCase().includes(q));
      if (matched.length > 0) {
        results.push({ category, modes: matched });
      }
    });
    return results;
  }, [searchQuery]);

  const handleDeleteSession = async (e, sessionId) => {
    e.stopPropagation();
    setDeletingId(sessionId);
    try {
      await axios.delete(`${API}/sessions/${sessionId}`);
      if (onSessionsChange) {
        onSessionsChange(sessions.filter(s => s.id !== sessionId));
      }
      if (currentSessionId === sessionId) {
        navigate('/chat');
      }
      toast.success('Session deleted');
    } catch {
      toast.error('Failed to delete session');
    } finally {
      setDeletingId(null);
    }
  };

  const handleNewChat = () => {
    if (onNewSession) {
      onNewSession();
    } else if (onNewChat) {
      onNewChat();
    }
  };

  const handleSessionClick = (session) => {
    if (onSessionSelect) {
      onSessionSelect(session);
    } else {
      navigate(`/chat/${session.id}`);
    }
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/20 z-40 lg:hidden"
          onClick={onClose || onToggle}
        />
      )}
      
      <aside
        data-testid="sidebar"
        className={`
          fixed lg:relative inset-y-0 left-0 z-50
          w-72 flex flex-col bg-white border-r border-slate-200 h-full
          transform transition-transform duration-200 ease-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
        style={{ backgroundColor: '#FFFFFF' }}
      >
        {/* Header */}
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-slate-900 flex items-center justify-center">
              <span className="text-white font-bold text-lg">G</span>
            </div>
            <div>
              <span className="text-sm font-bold text-slate-900 block">Gynovate AI/SI</span>
              <span className="text-xs text-slate-400">AI/SI Copilot for Doctors</span>
            </div>
          </div>
          <button
            onClick={onClose || onToggle}
            className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center lg:hidden"
          >
            <ChevronLeft className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        {/* New Chat Button */}
        <div className="p-4 pb-2">
          <button
            data-testid="new-chat-btn"
            onClick={handleNewChat}
            className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800 transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Chat
          </button>
        </div>

        {/* Search Bar */}
        <div className="px-4 pb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <input
              data-testid="sidebar-search-input"
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search modes..."
              className="w-full pl-9 pr-8 py-2 text-sm rounded-lg border border-slate-200 bg-slate-50 text-slate-700 placeholder-slate-400 focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-400 transition-colors"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-slate-200 text-slate-400"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        <ScrollArea className="flex-1 px-3">
          {/* Search Results */}
          {filteredModes !== null ? (
            <div className="mb-6">
              {filteredModes.length === 0 ? (
                <p className="px-3 py-4 text-sm text-slate-400 text-center">No modes found</p>
              ) : (
                filteredModes.map(group => (
                  <div key={group.category.id} className="mb-3">
                    <p className="px-2 py-1.5 text-xs font-medium text-slate-400 uppercase tracking-wider">
                      {group.category.name}
                    </p>
                    <div className="space-y-0.5">
                      {group.modes.map(mode => (
                        <button
                          key={mode.id}
                          data-testid={`search-result-${mode.id}`}
                          onClick={() => {
                            onModeSelect(mode.label);
                            setSearchQuery('');
                          }}
                          className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                            selectedMode === mode.label
                              ? 'bg-slate-100 text-slate-900 font-medium'
                              : 'text-slate-600 hover:bg-slate-50 hover:text-slate-700'
                          }`}
                        >
                          {mode.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
          <>
          {/* Mode Categories - Text Only */}
          <div className="mb-6">
            <p className="px-2 py-2 text-xs font-medium text-slate-400 uppercase tracking-wider">
              Categories
            </p>
            <div className="space-y-1">
              {MODE_CATEGORIES.map(category => (
                <div key={category.id}>
                  <button
                    onClick={() => setExpandedCategory(expandedCategory === category.id ? null : category.id)}
                    className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm hover:bg-slate-50 transition-colors"
                  >
                    <span className="font-medium text-slate-700">{category.name}</span>
                    <ChevronDown 
                      className={`w-4 h-4 text-slate-400 transition-transform ${
                        expandedCategory === category.id ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  
                  {expandedCategory === category.id && (
                    <div className="ml-3 pl-3 border-l border-slate-100 space-y-0.5 py-1">
                      {category.modes.map(mode => {
                        const isActive = selectedMode === mode.label;
                        return (
                          <button
                            key={mode.id}
                            data-testid={`sidebar-mode-${mode.id}`}
                            onClick={() => onModeSelect(mode.label)}
                            className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                              isActive
                                ? 'bg-slate-100 text-slate-900 font-medium'
                                : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
                            }`}
                          >
                            {mode.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Sessions removed for clean launch */}
          {/* Session History with date groups */}
          {sessions && sessions.length > 0 && (
            <div className="mb-4">
              <p className="px-2 py-2 text-xs font-medium text-slate-400 uppercase tracking-wider">Recent Chats</p>
              {(() => {
                const now = new Date();
                const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
                const weekAgo = new Date(today); weekAgo.setDate(weekAgo.getDate() - 7);
                const groups = [
                  { label: 'Today', filter: s => new Date(s.created_at) >= today },
                  { label: 'Yesterday', filter: s => { const d = new Date(s.created_at); return d >= yesterday && d < today; } },
                  { label: 'Earlier', filter: s => new Date(s.created_at) < yesterday },
                ];
                return groups.map(group => {
                  const grouped = sessions.filter(group.filter);
                  if (!grouped.length) return null;
                  return (
                    <div key={group.label} className="mb-2">
                      <p className="px-3 py-1 text-[10px] font-bold text-slate-300 uppercase tracking-widest">{group.label}</p>
                      <div className="space-y-0.5">
                        {grouped.map(session => (
                          <div
                            key={session.id}
                            data-testid={`session-item-${session.id}`}
                            onClick={() => handleSessionClick(session)}
                            className={`group flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                              currentSessionId === session.id ? 'bg-slate-100 text-slate-900' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
                            }`}
                          >
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium truncate">{session.title || session.mode || 'Chat'}</p>
                              <p className="text-[10px] text-slate-400">{timeAgo(session.created_at)}</p>
                            </div>
                            <button
                              data-testid={`delete-session-${session.id}`}
                              onClick={e => handleDeleteSession(e, session.id)}
                              disabled={deletingId === session.id}
                              className="opacity-0 group-hover:opacity-100 ml-2 p-1 rounded hover:bg-red-50 hover:text-red-500 transition-all flex-shrink-0"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          )}
          </>
          )}
        </ScrollArea>

        {/* Language Selector */}
        <div className="p-4 border-t border-slate-100">
          <div className="flex items-center gap-2 mb-2">
            <Globe className="w-3.5 h-3.5 text-slate-400" />
            <span className="text-xs text-slate-400 font-medium uppercase tracking-wider">Language</span>
          </div>
          <Select value={language} onValueChange={onLanguageChange}>
            <SelectTrigger className="h-10 text-sm rounded-lg border-slate-200 bg-slate-50">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <ScrollArea className="h-52">
                {LANGUAGES.map(lang => (
                  <SelectItem key={lang} value={lang} className="text-sm">{lang}</SelectItem>
                ))}
              </ScrollArea>
            </SelectContent>
          </Select>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
